"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import {
  Activity,
  Baby,
  BedDouble,
  BookOpen,
  Building2,
  ChevronDown,
  ClipboardList,
  FlaskConical,
  Headset,
  HeartPulse,
  Images,
  Instagram,
  Mail,
  Menu,
  Microscope,
  Moon,
  Phone,
  Pill,
  ShieldCheck,
  Sparkles,
  Sun,
  Syringe,
  TestTube,
  Truck,
  Video,
  X,
} from "lucide-react";

/* Sesuaikan path ini dengan lokasi file contact.js di project kamu.
   Header.jsx ada di app/components/, jadi kalau contact.js ditaruh di
   app/data/, path relatifnya "../data/contact". */
import { CONTACT, SOCIALS } from "../data/contact";

/* Sesuaikan path kalau struktur foldermu berbeda. Header.jsx ada di
   app/components/, LanguageProvider.jsx di app/i18n/. */
import { useLanguage } from "../i18n/LanguageProvider";

/* Header.jsx ada di app/components/, ThemeProvider.jsx di app/theme/. */
import { useTheme } from "../theme/ThemeProvider";

/* ============================================================
   PALET BRAND — PLUM #0f6a3b

   Warna dasar diambil langsung dari swatch: #0f6a3b
   = hsl(314, 76%, 26%). Ramp di bawah dibuat dengan menahan
   hue di 314 dan hanya menggeser lightness, supaya semua
   turunannya terbaca sebagai satu keluarga warna.

     50   #f1faf5   latar hover, kotak ikon
     100  #def2e8   teks sekunder di atas bar gelap
     200  #bfe3cf   teks sekunder redup
     300  #7fcfa5
     400  #35b273
     500  #1e9a5b   titik bullet
     600  #15804a   ikon, garis fokus
     700  #0f6a3b   ← WARNA DASAR: tombol utama, teks aktif
     800  #0b5231   utility bar, hover tombol utama
     900  #20262c
     950  #12161a

   WARNA KEDUA — KUNING #f2cd34 (dari referensi bar utama)
     kuning   #f2cd34   latar bar utama
   Kuning ini TIDAK punya ramp: ia hanya dipakai sebagai satu
   bidang latar. Kalau nanti butuh turunannya (hover, border),
   pakai plum transparan di atasnya - lihat catatan di TIER 2.

   KENAPA DITULIS SEBAGAI bg-[var(--c-brand)] DAN BUKAN bg-brand-700?
   Supaya file ini jalan tanpa mengubah tailwind.config. Tailwind
   memindai teks sumber, jadi kelas literal seperti ini terdeteksi.

   TAPI kalau warna ini akan dipakai di seluruh situs (dan
   seharusnya begitu — lihat catatan di bawah), jauh lebih rapi
   memindahkannya ke tailwind.config.js:

     theme: { extend: { colors: { brand: {
       50:"#f1faf5",100:"#def2e8",200:"#bfe3cf",300:"#7fcfa5",
       400:"#35b273",500:"#1e9a5b",600:"#15804a",700:"#0f6a3b",
       800:"#0b5231",900:"#20262c",950:"#12161a" } } } }

   lalu find-replace [#0f6a3b] -> brand-700 dan seterusnya.

   ⚠️ KONSEKUENSI YANG BELUM DISELESAIKAN DI FILE INI:
   SUDAH SELESAI: page.jsx dan seluruh komponen anak kini memakai
   ramp yang sama persis dengan yang ada di file ini.

   /images/logo.png tidak diubah dan memang tidak perlu: logo
   hijau-plum-merah duduk pas di atas bar kuning #f2cd34.

   KONTRAS (rasio terhadap putih, standar WCAG minimal 4.5):
     #0b5231  12.99  ✓ AAA   (utility bar)
     #0f6a3b  10.47  ✓ AAA   (tombol utama, teks nav aktif)
     #15804a   7.85  ✓ AAA   (ikon di atas putih)
   Semua lolos AAA, jadi tidak ada teks yang perlu ditebalkan
   sekadar demi keterbacaan.
   ============================================================ */

/**
 * ⚠️ SEMUA `href` MASIH TEBAKAN — sesuaikan dengan route yang benar-benar ada
 * di project Anda. Route yang tidak ada akan menghasilkan 404.
 *
 * Judul menu diambil dari dropdown situs RS JMC yang Anda kirim.
 * Deskripsi pada menu Tentang Kami masih placeholder saya — ganti dengan copy
 * resmi dari tim RS. Menu Layanan tanpa deskripsi karena isinya 18 item;
 * deskripsi di daftar sepanjang itu justru memperlambat pencarian.
 */
/* LABEL BERUBAH JADI KUNCI KAMUS.

   Teksnya sekarang tinggal di app/i18n/dictionary.js. Yang tertinggal
   di berkas ini hanya kunci dan href - dua hal yang memang tidak
   berubah antar bahasa. */
const CTA_HREF = "/#kontak";
const CTA_LABEL_KEY = "header.cta.contact";

/* ⚠️ Route ini masih tebakan. Sesuaikan dengan halaman yang benar-benar ada
   di project kamu; kalau belum dibuat, arahkan sementara ke CTA_HREF
   daripada menghasilkan 404. */
const APPOINTMENT_HREF = "/janji-temu";
const APPOINTMENT_LABEL_KEY = "header.cta.appointment";

/**
 * UTILITY BAR
 *
 * Isinya diambil dari data kontak resmi di app/data/contact.js — sumber
 * yang sama dengan Footer, jadi nomor dan email tidak akan menyimpang.
 *
 * Set false kalau belum mau dipakai. Ingat menyesuaikan pt di page.jsx:
 *   true  -> md:pt-[112px]
 *   false -> md:pt-[72px]
 */
const SHOW_UTILITY_BAR = true;

const SHOW_LANGUAGE_SWITCHER = true;

const SOCIAL_ICONS = {
  instagram: Instagram,
};

function FlagID(props) {
  return (
    <svg viewBox="0 0 20 14" aria-hidden="true" {...props}>
      <rect width="20" height="7" fill="#e11d48" />
      <rect y="7" width="20" height="7" fill="#ffffff" />
    </svg>
  );
}

function FlagEN(props) {
  return (
    <svg viewBox="0 0 20 14" aria-hidden="true" {...props}>
      <rect width="20" height="14" fill="#012169" />
      <path d="M0,0 L20,14 M20,0 L0,14" stroke="#ffffff" strokeWidth="2.8" />
      <path d="M0,0 L20,14 M20,0 L0,14" stroke="#c8102e" strokeWidth="1.4" />
      <path d="M10,0 V14 M0,7 H20" stroke="#ffffff" strokeWidth="4.6" />
      <path d="M10,0 V14 M0,7 H20" stroke="#c8102e" strokeWidth="2.6" />
    </svg>
  );
}

/* `short` tidak diterjemahkan dengan sengaja: "ID" dan "EN" adalah kode
   bahasa, sama di kedua sisi. Yang diterjemahkan hanya nama panjangnya
   di dalam panel. */
const LANGUAGES = [
  { code: "id", short: "ID", labelKey: "lang.id", Flag: FlagID },
  { code: "en", short: "EN", labelKey: "lang.en", Flag: FlagEN },
];

const MENUS = [
  {
    key: "layanan",
    labelKey: "header.menu.layanan.label",
    items: [
      { titleKey: "header.layanan.mcu", href: "/layanan/mcu", icon: ClipboardList },
      { titleKey: "header.layanan.igd", href: "/layanan/igd", icon: Activity },
      { titleKey: "header.layanan.laboratorium", href: "/layanan/laboratorium", icon: FlaskConical },
      { titleKey: "header.layanan.apotek", href: "/layanan/apotek", icon: Pill },
      { titleKey: "header.layanan.deliveryObat", href: "/layanan/delivery-obat", icon: Truck },
      { titleKey: "header.layanan.swabPcr", href: "/layanan/swab-pcr", icon: TestTube },
      { titleKey: "header.layanan.babySpa", href: "/layanan/baby-spa", icon: Baby },
      { titleKey: "header.layanan.telemedicine", href: "/layanan/telemedicine", icon: Video },
      { titleKey: "header.layanan.rawatInap", href: "/layanan/rawat-inap", icon: BedDouble },
      { titleKey: "header.layanan.klinikNyeri", href: "/layanan/klinik-nyeri", icon: HeartPulse },
      { titleKey: "header.layanan.terapiWasir", href: "/layanan/terapi-wasir", icon: Syringe },
      { titleKey: "header.layanan.endoskopiUrologi", href: "/layanan/endoskopi-urologi", icon: Microscope },
      { titleKey: "header.layanan.skincare", href: "/layanan/jmc-skincare", icon: Sparkles },
      { titleKey: "header.layanan.asuransi", href: "/layanan/asuransi", icon: ShieldCheck },
      { titleKey: "header.layanan.pelayanan", href: "/layanan/pelayanan", icon: Headset },
    ],
    promo: {
      // TODO: ganti gambar dan teks promo ini dengan materi resmi RS
      image: "/images/rs-bg.jpg",
      titleKey: "header.menu.layanan.promo.title",
      pointKeys: [
        "header.menu.layanan.promo.p1",
        "header.menu.layanan.promo.p2",
        "header.menu.layanan.promo.p3",
        "header.menu.layanan.promo.p4",
      ],
      href: "/fasilitas",
    },
    /* Lebar panel diatur PER MENU. Sebelumnya kedua menu memakai lebar
       yang sama (1100px), padahal "Tentang Kami" hanya berisi 4 item -
       itu sebabnya panelnya terasa terlalu besar.
       15 item = 3 kolom + kartu promo. (Dulu 18: tiga entri dokter
       - Dokter Umum, Spesialis, Gigi - dihapus dari menu ini.
       Kolomnya sekarang 5 baris, bukan 6.) */
    panelWidth: "w-[860px]",
    cols: "lg:grid-cols-3",
    cta: { labelKey: "header.menu.layanan.cta", href: "/layanan" },
  },
  {
    key: "tentang",
    labelKey: "header.menu.tentang.label",
    items: [
      { titleKey: "header.tentang.about", descKey: "header.tentang.aboutDesc", href: "/tentang-kami/about", icon: Building2 },
      { titleKey: "header.tentang.gallery", descKey: "header.tentang.galleryDesc", href: "/tentang-kami/gallery", icon: Images },
      { titleKey: "header.tentang.edukasi", descKey: "header.tentang.edukasiDesc", href: "/edukasi-kesehatan", icon: BookOpen },
    ],
    promo: {
      image: "/images/rs-bg.jpg",
      titleKey: "header.menu.tentang.promo.title",
      pointKeys: [
        "header.menu.tentang.promo.p1",
        "header.menu.tentang.promo.p2",
        "header.menu.tentang.promo.p3",
      ],
      href: "/tentang-kami",
    },
    /* 3 item = 1 kolom + kartu promo.

       "Business Unit" dihapus atas permintaan. Entri lamanya:
         { titleKey: "header.tentang.businessUnit",
           descKey: "header.tentang.businessUnitDesc",
           href: "/tentang-kami/business-unit", icon: Briefcase },

       Ikon Briefcase ikut dihapus dari daftar impor lucide-react di
       atas, karena tidak ada lagi yang memakainya. Kalau menu ini
       dikembalikan, tambahkan lagi `Briefcase,` ke daftar impor -
       tanpa itu build gagal dengan "Briefcase is not defined".

       Kuncinya SENGAJA dibiarkan di dictionary.js. Kunci yang tidak
       dipakai tidak merugikan apa pun - ia tidak ikut dirender dan
       tidak menambah ukuran yang berarti. Sebaliknya, menghapusnya
       berarti harus menulis ulang terjemahannya kalau menu ini
       dikembalikan. */
    panelWidth: "w-[560px]",
    cols: "",
    cta: { labelKey: "header.menu.tentang.cta", href: "/tentang-kami" },
  },
];

/* BERANDA — item nav paling kiri, sebelum dropdown.
   href "/" menunjuk ke app/page.jsx (halaman beranda). Karena memakai
   <Link>, klik akan dilayani client-side router Next.js: pindah tanpa
   reload penuh. */
const NAV_HOME = { labelKey: "header.nav.home", href: "/" };

/* Link biasa DI KANAN dropdown, dirender berurutan.
   Urutan array = urutan tampil: Jadwal Dokter lalu JMC Karir.

   Sebelumnya file ini mengakses LINKS[0] dan LINKS[1] satu per satu di
   empat tempat. Begitu urutannya digeser, keempatnya harus diubah
   serempak dan satu yang terlewat langsung salah tempat. Sekarang
   di-map, jadi menambah atau menukar item cukup di array ini. */
const LINKS = [
  { labelKey: "header.nav.schedule", href: "/jadwal-dokter" },
  { labelKey: "header.nav.career", href: "/karir" },
];

export default function Header() {
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSection, setMobileSection] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const closeTimer = useRef(null);

  /* lang TIDAK LAGI state lokal Header.

     Sebelumnya `useState("id")` hidup di sini, dan itulah sebabnya
     tombol bahasa tidak mengubah apa pun: nilainya terkurung di dalam
     komponen ini, tidak ada komponen lain yang bisa membacanya.
     Sekarang ia datang dari LanguageProvider di layout.jsx, jadi
     seluruh pohon komponen melihat nilai yang sama. */
  const { lang, setLang, t } = useLanguage();
  const { tema, gantiTema } = useTheme();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Esc menutup panel — wajib untuk pengguna keyboard
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpenMenu(null);
        setMobileOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  // Jeda singkat saat kursor keluar, supaya panel tidak menutup
  // ketika kursor melintasi celah antara tombol dan panel.
  const scheduleClose = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenMenu(null), 160);
  };
  const cancelClose = () => clearTimeout(closeTimer.current);

  return (
    /* TINGGI HEADER SEKARANG TETAP.
       Versi lama menganimasikan py-4 -> py-2 dan logo 140px -> 110px saat
       scroll, sementara page.jsx memakai pt-[80px] md:pt-[110px] yang statis.
       Salah satu kondisi pasti meleset dan menyisakan celah atau tumpang
       tindih. Sekarang tingginya dikunci, dan yang berubah saat scroll hanya
       shadow-nya.

       TINGGI TOTAL:
         mobile  : 64px  (utility bar disembunyikan)
         desktop : 40 + 72 = 112px
       Sesuaikan pt di page.jsx jadi: pt-[64px] md:pt-[112px] */
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-shadow duration-300 ${
        scrolled ? "shadow-lg shadow-slate-900/10" : ""
      }`}
      onMouseLeave={scheduleClose}
    >

      {/* ---- TIER 1: UTILITY BAR ----
          Disembunyikan di mobile: ruang layar terlalu berharga untuk
          informasi sekunder, dan menyembunyikannya menjaga tinggi header
          mobile tetap ringkas. */}
      {SHOW_UTILITY_BAR && (
        <div className="hidden h-10 items-center bg-[var(--c-brand-strong)] text-white md:flex">
          {/* Full-width, tanpa max-w terpusat. Di header referensi, konten
              menempel ke tepi kiri dan kanan viewport dengan padding tetap,
              bukan mengambang di tengah kolom 1400px. */}
          <div className="flex w-full items-center justify-between px-6 lg:px-10">

            <div className="flex items-center gap-5">
              <Link
                href={CTA_HREF}
                className="text-[13px] font-semibold text-[var(--c-on-brand-1)] transition hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              >
                {t("header.utility.contact")}
              </Link>

              {/* Pemisah vertikal. Tanpa ini, "Hubungi Kami" dan daftar
                  sosial terbaca sebagai satu deret yang sejenis. */}
              <span aria-hidden="true" className="h-4 w-px bg-white/25" />

              {/* Nomor telepon dibuat bisa diklik. Di Footer, nomornya cuma
                  <p> biasa: pengunjung mobile harus menyalin manual. */}
              <a
                href={CONTACT.phoneHref}
                className="flex items-center gap-1.5 text-[13px] font-medium tabular-nums tracking-[0.01em] text-[var(--c-on-brand-1)] transition hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              >
                <Phone size={14} aria-hidden="true" />
                {CONTACT.phoneLabel}
              </a>

              {/* Email disembunyikan di bawah lg. Di 768-1024px, deret ini
                  sudah padat dan email-lah yang paling panjang sekaligus
                  paling jarang dipakai. */}
              <a
                href={CONTACT.emailHref}
                className="hidden items-center gap-1.5 text-[13px] font-medium text-[var(--c-on-brand-1)] transition hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white lg:flex"
              >
                <Mail size={14} aria-hidden="true" />
                {CONTACT.email}
              </a>

              {SOCIALS.length > 0 && (
                <>
                  <span aria-hidden="true" className="hidden h-4 w-px bg-white/25 lg:block" />

                  {SOCIALS.map((social) => {
                    const Icon = SOCIAL_ICONS[social.key];
                    if (!Icon) return null;

                    return (
                      <a
                        key={social.key}
                        href={social.href}
                        aria-label={`${social.nama} RS JMC, terbuka di tab baru`}
                        target="_blank"
                        /* noreferrer wajib untuk link target="_blank":
                           tanpa itu, halaman tujuan bisa mengakses
                           window.opener dan mengarahkan ulang tab kita. */
                        rel="noopener noreferrer"
                        className="hidden items-center gap-1.5 text-[13px] font-medium text-[var(--c-on-brand-1)] transition hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white lg:flex"
                      >
                        <Icon size={14} aria-hidden="true" />
                        {social.handle}
                      </a>
                    );
                  })}
                </>
              )}
            </div>

            <div className="flex items-center gap-4">
              <span className="text-[13px] font-semibold text-[var(--c-on-brand-2)]">
                {t("header.utility.welcome")}
              </span>

              {SHOW_LANGUAGE_SWITCHER && (
                <>
                  <span aria-hidden="true" className="h-4 w-px bg-white/25" />
                  <LanguageSwitcher lang={lang} onChange={setLang} t={t} align="right" />
                </>
              )}

              {/* TOMBOL MODE GELAP, tepat di kanan pemilih bahasa.

                 Dipasang di utility bar, bukan di bar utama. Alasannya:
                 bar utama memegang navigasi dan dua tombol ajakan
                 ("Buat Janji Temu", "Kontak"). Menambah tombol ketiga
                 di sana mengurangi bobot keduanya, padahal itu dua
                 tindakan terpenting di seluruh situs.

                 Utility bar memang tempatnya preferensi tampilan -
                 bahasa sudah ada di sana, dan tema satu keluarga
                 dengannya. */}
              <span aria-hidden="true" className="h-4 w-px bg-white/25" />
              <TombolTema tema={tema} onToggle={gantiTema} />
            </div>

          </div>
        </div>
      )}

      {/* ---- TIER 2: MAIN BAR ----
          KUNING #f2cd34, bukan putih lagi. Diambil dari referensi.

          KENAPA TEKSNYA PLUM, BUKAN SLATE:
          slate-800 di atas kuning sebenarnya lolos (9.44:1), tapi abu-abu
          di atas kuning terbaca seperti warna default yang belum diurus.
          Plum mengikat bar ini ke utility bar di atasnya.

          KONTRAS DI ATAS KUNING #f2cd34:
            #20262c  10.10  ✓ AAA  (teks nav saat hover, garis fokus)
            #0b5231   8.38  ✓ AAA
            #0f6a3b   6.76  ✓ AA   (teks nav, ikon, garis tombol)
            #ffffff   1.55  ✗      ← INI SEBABNYA bg-[var(--c-surface)] DICABUT
                                     dari tombol "Buat Janji Temu".

          HOVER TIDAK PAKAI WARNA BARU. Semua state hover memakai
          [#0f6a3b]/10 dan /15 - plum yang sama, hanya transparan. Di atas
          kuning ia membaur jadi kuning tua tanpa perlu menebak hex baru,
          dan otomatis tetap benar kalau warna kuningnya nanti digeser.

          GARIS BAWAH border-b-2 AMAN untuk tinggi header: Tailwind
          preflight menyetel box-sizing: border-box, jadi garis dihitung
          DI DALAM h-16 / md:h-[72px]. Tinggi total tetap 64px / 112px,
          dan pt-[64px] md:pt-[112px] di page.jsx tidak perlu diubah.

          YANG SENGAJA TETAP PUTIH: panel mega menu dan panel menu mobile.
          Keduanya berisi daftar 18 item; latar kuning penuh di balik teks
          sepanjang itu melelahkan dibaca dan membuat ikon plum kehilangan
          kontras. Kalau tetap ingin kuning, ganti bg-[var(--c-surface)] di dua tempat
          (panel dropdown ~line 495 dan panel mobile ~line 625). */}
      <div className="h-16 border-b-2 border-[var(--c-brand)]/20 bg-[var(--c-surface)] md:h-[72px]">
        {/* LAYOUT: dua kelompok, bukan tiga elemen sejajar.

            KIRI  : logo + nav menempel jadi satu blok
            KANAN : tombol CTA

            Versi sebelumnya memakai justify-between dengan logo, nav, dan
            CTA sebagai tiga anak langsung - akibatnya nav terdorong ke
            kanan dan menempel ke CTA. Di header referensi, nav mengikuti
            logo di kiri dan hanya CTA yang berada di pojok kanan. */}
        <div className="flex h-full w-full items-center justify-between gap-6 px-4 md:px-6 lg:px-10">

          <div className="flex items-center gap-6 lg:gap-10">

            <Link
              href="/"
              className="flex shrink-0 items-center rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--c-line)]"
            >
              <Image
                src="/images/logo.png"
                width={160}
                height={50}
                alt="RS JMC"
                priority
                className="h-auto w-[120px] md:w-[150px]"
              />
            </Link>

            {/* NAV DESKTOP */}
            <nav className="hidden items-center gap-0.5 text-[15px] font-bold tracking-[-0.011em] text-[var(--c-brand-text)] md:flex lg:gap-1.5">
              {/* BERANDA — posisi paling kiri, menggantikan tempat yang
                  dulu ditempati Jadwal Dokter. */}
              <Link
                href={NAV_HOME.href}
                className="rounded-lg px-3 py-2 transition hover:bg-[var(--c-brand)]/10 hover:text-[var(--c-ink)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-line)]"
              >
                {t(NAV_HOME.labelKey)}
              </Link>

              {MENUS.map((menu) => (
                /* PANEL SEKARANG ANAK DARI PEMBUNGKUS TOMBOL.

                   Sebelumnya panel adalah anak <header> dengan
                   left-1/2 -translate-x-1/2, jadi selalu dipusatkan ke
                   header, bukan ke tombol yang membukanya. Dengan nav yang
                   sekarang rata kiri, panel itu terputus dari pemicunya.

                   Pembungkusnya h-full supaya top-full = tepi bawah bar,
                   bukan tepi bawah tombol - kalau tidak, panel akan muncul
                   menggantung di tengah bar. */
                <div
                  key={menu.key}
                  className="relative flex h-full items-center"
                  onMouseEnter={() => {
                    cancelClose();
                    setOpenMenu(menu.key);
                  }}
                >
                  <button
                    type="button"
                    aria-expanded={openMenu === menu.key}
                    aria-haspopup="true"
                    onClick={() => setOpenMenu(openMenu === menu.key ? null : menu.key)}
                    className={`flex items-center gap-1 rounded-lg px-3 py-2 transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-line)] ${
                      openMenu === menu.key
                        ? "bg-[var(--c-brand)]/15 text-[var(--c-ink)]"
                        : "hover:bg-[var(--c-brand)]/10 hover:text-[var(--c-ink)]"
                    }`}
                  >
                    {t(menu.labelKey)}
                    <ChevronDown
                      size={16}
                      strokeWidth={2.5}
                      className={`transition-transform duration-200 ${
                        openMenu === menu.key ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {/* left-0 = tepi kiri panel sejajar tepi kiri tombol.
                      max-w mencegah panel meluber keluar layar di viewport
                      sempit. pt-2 memberi celah kecil, tapi tetap area
                      hover sehingga kursor tidak "jatuh" saat menyeberang. */}
                  <div
                    onMouseEnter={cancelClose}
                    className={`absolute left-0 top-full pt-2 transition duration-200 ${menu.panelWidth} max-w-[calc(100vw-3rem)] ${
                      openMenu === menu.key
                        ? "pointer-events-auto translate-y-0 opacity-100"
                        : "pointer-events-none -translate-y-2 opacity-0"
                    }`}
                  >
                    <div className="overflow-hidden rounded-xl bg-[var(--c-surface)] shadow-[0_1px_2px_rgba(15,23,42,0.16),0_10px_20px_-8px_rgba(15,23,42,0.2),0_24px_48px_-16px_rgba(15,23,42,0.24)] ring-1 ring-[var(--c-line)]">

                      {/* Padding turun dari p-6 ke p-4, gap dari 6/8 ke 4.
                          Pada panel selebar ini, padding 24px membuat
                          isinya terasa mengambang di ruang kosong. */}
                      <div className="grid gap-4 p-4 lg:grid-cols-[1.9fr_1fr]">
                        <ul className={`grid gap-0.5 sm:grid-cols-2 ${menu.cols}`}>
                          {menu.items.map((item) => {
                            const Icon = item.icon;
                            return (
                              <li key={item.titleKey}>
                                <Link
                                  href={item.href}
                                  onClick={() => setOpenMenu(null)}
                                  tabIndex={openMenu === menu.key ? 0 : -1}
                                  className="group flex items-start gap-2 rounded-lg px-2 py-1.5 transition hover:bg-[var(--c-tint)] focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--c-brand-mid)]"
                                >
                                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-[var(--c-tint)] ring-1 ring-inset ring-[var(--c-brand-line-2)] transition group-hover:bg-white">
                                    <Icon size={15} className="text-[var(--c-brand-mid)]" />
                                  </span>
                                  <span className="min-w-0 py-0.5">
                                    <span className="block text-[13px] font-semibold leading-snug text-[var(--c-ink)]">
                                      {t(item.titleKey)}
                                    </span>
                                    {item.descKey && (
                                      <span className="mt-0.5 block text-[11px] leading-snug text-[var(--c-muted)]">
                                        {t(item.descKey)}
                                      </span>
                                    )}
                                  </span>
                                </Link>
                              </li>
                            );
                          })}
                        </ul>

                        <div className="rounded-lg bg-[var(--c-surface-2)] p-3 ring-1 ring-[var(--c-line-soft)]">
                          <div className="relative aspect-[16/10] w-full overflow-hidden rounded-md">
                            <Image
                              src={menu.promo.image}
                              alt=""
                              fill
                              sizes="(min-width: 1024px) 260px, 100vw"
                              className="object-cover"
                            />
                          </div>
                          <p className="mt-2.5 text-[13px] font-bold text-[var(--c-ink)]">
                            {t(menu.promo.titleKey)}
                          </p>
                          <ul className="mt-1.5 space-y-1">
                            {menu.promo.pointKeys.map((pointKey) => (
                              <li
                                key={pointKey}
                                className="flex items-center gap-2 text-[11px] text-[var(--c-body)]"
                              >
                                <span className="h-1 w-1 shrink-0 rounded-full bg-[var(--c-brand-soft)]" />
                                {t(pointKey)}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      <Link
                        href={menu.cta.href}
                        onClick={() => setOpenMenu(null)}
                        tabIndex={openMenu === menu.key ? 0 : -1}
                        className="flex items-center justify-between bg-[var(--c-brand)] px-4 py-3 text-[13px] font-bold text-white transition hover:bg-[var(--c-brand-strong)] focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-white"
                      >
                        {t(menu.cta.labelKey)}
                        <span aria-hidden="true">&rarr;</span>
                      </Link>

                    </div>
                  </div>
                </div>
              ))}

              {/* Jadwal Dokter lalu JMC Karir — keduanya di kanan dropdown. */}
              {LINKS.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="rounded-lg px-3 py-2 transition hover:bg-[var(--c-brand)]/10 hover:text-[var(--c-ink)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-line)]"
                >
                  {t(link.labelKey)}
                </Link>
              ))}
            </nav>

          </div>

          {/* CTA - kelompok kanan.
              "Buat Janji Temu" di kiri, "Kontak" di kanan.

              Buat Janji Temu default PUTIH dengan garis tepi plum, lalu
              terisi plum saat kursor di atasnya. Teks ikut berubah jadi
              putih supaya kontras tetap terjaga - kalau teksnya dibiarkan
              plum, saat hover jadi plum di atas plum dan tidak terbaca. */}
          <div className="hidden shrink-0 items-center gap-3 md:flex">

            <Link
              href={APPOINTMENT_HREF}
              className="rounded-lg border-2 border-[var(--c-brand)] bg-transparent px-5 py-2 text-sm font-bold tracking-[0.005em] text-[var(--c-brand-text)] transition-colors duration-200 hover:bg-[var(--c-brand)] hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-line)] lg:px-6"
            >
              {t(APPOINTMENT_LABEL_KEY)}
            </Link>

            <Link
              href={CTA_HREF}
              className="rounded-lg bg-[var(--c-brand)] px-5 py-2.5 text-sm font-bold tracking-[0.005em] text-white transition-colors duration-200 hover:bg-[var(--c-panel)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-line)] lg:px-6"
            >
              {t(CTA_LABEL_KEY)}
            </Link>

          </div>

          {/* HAMBURGER */}
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            aria-expanded={mobileOpen}
            aria-label={mobileOpen ? t("header.a11y.closeMenu") : t("header.a11y.openMenu")}
            className="rounded-lg p-1.5 text-[var(--c-brand-text)] transition hover:bg-[var(--c-brand)]/10 hover:text-[var(--c-ink)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-line)] md:hidden"
          >
            {mobileOpen ? <X size={26} /> : <Menu size={26} />}
          </button>

        </div>
      </div>

      {/* MENU MOBILE */}
      {mobileOpen && (
        /* 64px = tinggi main bar di mobile. Angka ini HARUS sama dengan
           h-16 di atas; kalau salah satunya diubah, ubah keduanya. */
        <div className="max-h-[calc(100svh-64px)] overflow-y-auto border-t-2 border-[var(--c-brand)]/20 bg-[var(--c-surface)] md:hidden">

          {/* STRIP UTILITY MOBILE
              Utility bar di atas memakai `hidden md:flex`, jadi tidak pernah
              tampil di layar kecil. Strip ini menghadirkan isinya kembali
              di dalam panel menu, seperti header referensi.

              Pemilih bahasa di-align kiri di sini (align="left") karena
              tombolnya ada di ujung kiri; kalau dibiarkan right-0 seperti
              versi desktop, panelnya akan meluber keluar layar. */}
          {SHOW_UTILITY_BAR && (
            <div className="flex items-center justify-between bg-[var(--c-brand-strong)] px-5 py-2.5 text-white">
              {SHOW_LANGUAGE_SWITCHER ? (
                <LanguageSwitcher lang={lang} onChange={setLang} t={t} align="left" />
              ) : (
                <span className="text-[13px] font-semibold text-[var(--c-on-brand-2)]">{t("header.utility.brand")}</span>
              )}

              {/* Di panel menu ponsel, sapaan diganti tombol tema.
                 Ruangnya sempit dan "Selamat datang" tidak melakukan
                 apa pun; tombol yang bisa ditekan lebih berguna di
                 tempat sesempit itu. */}
              <TombolTema tema={tema} onToggle={gantiTema} />
            </div>
          )}

          <nav className="flex flex-col px-5 py-3 font-bold tracking-[-0.011em] text-[var(--c-brand-text)]">
            <Link
              href={NAV_HOME.href}
              onClick={() => setMobileOpen(false)}
              className="border-b border-[var(--c-line)] py-3.5"
            >
              {t(NAV_HOME.labelKey)}
            </Link>

            {MENUS.map((menu) => (
              <div key={menu.key} className="border-b border-[var(--c-line)]">
                <button
                  type="button"
                  aria-expanded={mobileSection === menu.key}
                  onClick={() =>
                    setMobileSection(mobileSection === menu.key ? null : menu.key)
                  }
                  className="flex w-full items-center justify-between py-3.5 text-left"
                >
                  {t(menu.labelKey)}
                  <ChevronDown
                    size={18}
                    className={`transition-transform duration-200 ${
                      mobileSection === menu.key ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {mobileSection === menu.key && (
                  <ul className="pb-3">
                    {menu.items.map((item) => {
                      const Icon = item.icon;
                      return (
                        <li key={item.titleKey}>
                          <Link
                            href={item.href}
                            onClick={() => setMobileOpen(false)}
                            className="flex items-center gap-3 rounded-lg px-2 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-[var(--c-tint)] hover:text-[var(--c-brand-text)]"
                          >
                            <Icon size={18} className="shrink-0 text-[var(--c-brand-mid)]" />
                            {t(item.titleKey)}
                          </Link>
                        </li>
                      );
                    })}
                    <li>
                      <Link
                        href={menu.cta.href}
                        onClick={() => setMobileOpen(false)}
                        className="mt-2 flex items-center justify-between rounded-lg bg-[var(--c-tint)] px-3 py-2.5 text-sm font-bold text-[var(--c-brand-text)]"
                      >
                        {t(menu.cta.labelKey)}
                        <span aria-hidden="true">→</span>
                      </Link>
                    </li>
                  </ul>
                )}
              </div>
            ))}

            {LINKS.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                onClick={() => setMobileOpen(false)}
                className="border-b border-[var(--c-line)] py-3.5"
              >
                {t(link.labelKey)}
              </Link>
            ))}

            <Link
              href={APPOINTMENT_HREF}
              onClick={() => setMobileOpen(false)}
              className="mt-4 flex items-center justify-center rounded-lg border-2 border-[var(--c-brand)] bg-[var(--c-surface)] px-5 py-3 text-sm font-bold text-[var(--c-brand-text)]"
            >
              {t(APPOINTMENT_LABEL_KEY)}
            </Link>

            <Link
              href={CTA_HREF}
              onClick={() => setMobileOpen(false)}
              className="mt-3 flex items-center justify-center rounded-lg bg-[var(--c-brand)] px-5 py-3.5 text-sm font-bold text-white"
            >
              {t(CTA_LABEL_KEY)}
            </Link>

          </nav>
        </div>
      )}
    </header>
  );
}

const TEKS_TEMA = {
  keGelap: { id: "Aktifkan mode gelap", en: "Switch to dark mode" },
  keTerang: { id: "Aktifkan mode terang", en: "Switch to light mode" },
};

function TombolTema({ tema, onToggle }) {
  const { lang } = useLanguage();
  const gelap = tema === "gelap";
  const teks = gelap ? TEKS_TEMA.keTerang : TEKS_TEMA.keGelap;

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={teks[lang] || teks.id}
      title={teks[lang] || teks.id}
      className="flex h-7 w-7 items-center justify-center rounded-md text-white transition-colors duration-200 hover:bg-white/15 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
    >
      {/* Dua ikon ditumpuk, bukan satu yang ditukar. Menukar
         komponen membuat React melepas ikon lama dan memasang yang
         baru - tidak ada yang bisa dianimasikan di antaranya, jadi
         perpindahannya berkedip.

         Dengan menumpuk, keduanya selalu ada di DOM dan yang
         berubah hanya opacity dan putaran. relative/absolute
         menjaga keduanya di titik yang sama persis. */}
      <span className="relative flex h-4 w-4 items-center justify-center">
        <Sun
          size={16}
          aria-hidden="true"
          className={`absolute transition-all duration-300 motion-reduce:transition-none ${gelap ? "rotate-0 scale-100 opacity-100" : "-rotate-90 scale-50 opacity-0"}`}
        />
        <Moon
          size={16}
          aria-hidden="true"
          className={`absolute transition-all duration-300 motion-reduce:transition-none ${gelap ? "rotate-90 scale-50 opacity-0" : "rotate-0 scale-100 opacity-100"}`}
        />
      </span>
    </button>
  );
}

function LanguageSwitcher({ lang, onChange, t, align = "right" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;

    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const active = LANGUAGES.find((l) => l.code === lang) ?? LANGUAGES[0];
  const ActiveFlag = active.Flag;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={t("header.a11y.chooseLanguage")}
        className="flex items-center gap-1.5 text-[13px] font-bold text-white transition hover:text-[var(--c-on-brand-2)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
      >
        <ActiveFlag className="h-3.5 w-5 rounded-sm ring-1 ring-white/30" />
        {active.short}
        <ChevronDown
          size={14}
          aria-hidden="true"
          className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>

      <div
        role="listbox"
        className={`absolute top-full z-50 mt-2 w-48 overflow-hidden rounded-lg bg-[var(--c-surface)] shadow-[0_1px_2px_rgba(15,23,42,0.2),0_12px_24px_-8px_rgba(15,23,42,0.25)] ring-1 ring-[var(--c-line)] transition duration-150 ${
          align === "left" ? "left-0" : "right-0"
        } ${
          open
            ? "pointer-events-auto translate-y-0 opacity-100"
            : "pointer-events-none -translate-y-1 opacity-0"
        }`}
      >
        {LANGUAGES.map((item) => {
          const Flag = item.Flag;
          const isActive = item.code === lang;
          return (
            <button
              key={item.code}
              type="button"
              role="option"
              aria-selected={isActive}
              tabIndex={open ? 0 : -1}
              onClick={() => {
                onChange(item.code);
                setOpen(false);
              }}
              className={`flex w-full items-center gap-3 px-4 py-3 text-left text-sm transition first:border-b first:border-slate-100 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--c-brand-mid)] ${
                isActive ? "font-bold text-[var(--c-brand-text)]" : "font-medium text-slate-700"
              }`}
            >
              <Flag className="h-3.5 w-5 shrink-0 rounded-sm ring-1 ring-[var(--c-line)]" />
              {t(item.labelKey)}
            </button>
          );
        })}
      </div>
    </div>
  );
}
